import { ANALYTICS_CONFIG } from '../config/analytics';
import api from '../config/api';

const STORAGE_KEYS = {
    CLIENT_ID: 'analytics_client_id',
    SESSION_START: 'session_start_time'
};

// Session timeout in minutes
const SESSION_TIMEOUT = 30;

/**
 * Service for handling analytics events
 */
class AnalyticsService {
    constructor() {
        /** @type {Array<import('../types/analytics').AnalyticsEvent>} */
        this.eventQueue = [];
        /** @type {NodeJS.Timeout | null} */
        this.flushTimeout = null;
        this.clientId = this.getOrCreateClientId();
        this.sessionId = this.getOrCreateSessionId();
        this.startFlushInterval();
    }

    /**
     * Starts the interval to flush events periodically
     * @private
     */
    startFlushInterval() {
        this.flushTimeout = setInterval(() => {
            this.flush();
        }, ANALYTICS_CONFIG.FLUSH_INTERVAL);
    }

    /**
     * Flushes queued events to the analytics endpoint
     * @private
     */
    async flush() {
        if (this.eventQueue.length === 0) return;

        try {
            const events = [...this.eventQueue];
            this.eventQueue = [];

            const response = await api.post(
                ANALYTICS_CONFIG.ENDPOINT,
                { events }
            );

            if (ANALYTICS_CONFIG.DEBUG) {
                console.log('Analytics events sent:', events);
                console.log('Response:', response);
            }
        } catch (error) {
            console.error('Failed to send analytics events:', error);
            // Re-queue failed events
            this.eventQueue = [...this.eventQueue, ...this.eventQueue];
        }
    }

    /**
     * Tracks an analytics event
     * @param {string} eventName - Name of the event
     * @param {import('../types/analytics').EventCategory} eventCategory - Category of the event
     * @param {Record<string, any>} [properties] - Additional properties for the event
     */
    async trackEvent(eventName, eventCategory, properties = {}) {
        this.updateSession();

        const event = {
            event_name: eventName,
            event_category: eventCategory,
            event_data: {
                timestamp: Date.now(),
                properties,
            },
            client_id: this.clientId,
            session_id: this.sessionId
        };

        this.eventQueue.push(event);

        if (this.eventQueue.length >= ANALYTICS_CONFIG.BATCH_SIZE) {
            await this.flush();
        }
    }

    /**
     * Cleans up the service
     */
    destroy() {
        if (this.flushTimeout) {
            clearInterval(this.flushTimeout);
        }
        this.flush();
    }

    getOrCreateClientId() {
        let clientId = localStorage.getItem(STORAGE_KEYS.CLIENT_ID);
        if (!clientId) {
            clientId = 'cid_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem(STORAGE_KEYS.CLIENT_ID, clientId);
        }
        return clientId;
    }

    getOrCreateSessionId() {
        const lastActivity = localStorage.getItem(STORAGE_KEYS.SESSION_START);
        const currentTime = Date.now();

        if (!lastActivity || (currentTime - parseInt(lastActivity)) > SESSION_TIMEOUT * 60 * 1000) {
            // Create new session
            const sessionId = 'sid_' + currentTime + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem(STORAGE_KEYS.SESSION_START, currentTime.toString());
            return sessionId;
        }

        // Update last activity
        localStorage.setItem(STORAGE_KEYS.SESSION_START, currentTime.toString());
        return 'sid_' + lastActivity + '_' + Math.random().toString(36).substr(2, 9);
    }

    updateSession() {
        const currentTime = Date.now();
        const lastActivity = localStorage.getItem(STORAGE_KEYS.SESSION_START);

        if (!lastActivity || (currentTime - parseInt(lastActivity)) > SESSION_TIMEOUT * 60 * 1000) {
            this.sessionId = this.getOrCreateSessionId();
        }
        localStorage.setItem(STORAGE_KEYS.SESSION_START, currentTime.toString());
    }
}

export const analyticsService = new AnalyticsService(); 